'use client';

import React from 'react';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { PharmacyBookingModalProvider } from '@/features/pharamacy/components/PharmacyBookingModalContext/PharmacyBookingModalContext';
const Footer = dynamic(() => import('@/components/organisms/Footer/Footer'), {
  ssr: true,
  loading: () => (
    <div className="bg-surface-soft min-h-[200px] w-full" aria-hidden="true" />
  ),
});
import Header from '@/components/layout/Header/Header';
import { LazyLocationSection } from '@/components/layout/LocationSection';

import { useScrollAnimation } from './hooks/useScrollAnimation';

const FloatingMobileActions = dynamic(
  () => import('@/components/layout/FloatingMobileActions'),
  { ssr: false, loading: () => null },
);

const LazyNewsletterSection = dynamic(
  () => import('@/components/layout/NewsLetter/Newsletter'),
  { ssr: false, loading: () => null },
);

interface MainLayoutProps {
  children: React.ReactNode;
  hasHero?: boolean;
  className?: string;
  isLandingPage?: boolean;
  showLocation?: boolean;
  showNewsletter?: boolean;
  email?: string;
  phone?: string;
  btnTitle?: string;
}

export type { MainLayoutProps };

/**
 * Main Layout Component
 *
 * This layout component provides a consistent structure across the application with:
 * - Header integration (overlay mode for pages with hero sections)
 * - Main content area
 * - Footer
 *
 * **Header Integration Patterns:**
 *
 * 1. **Hero Pages (hasHero=true)**:
 *    - Header is positioned absolutely over the hero section
 *    - Uses `isOverlay=true` for transparent/overlay styling
 *    - No separate top partition - integrates seamlessly with hero
 *    - Hero section provides the background and visual impact
 *
 * 2. **Standard Pages (hasHero=false)**:
 *    - Header takes its own space at the top
 *    - Uses standard header styling with background
 *    - Creates clear separation between header and content
 *
 * **Why this pattern works:**
 * - Modern web design trend for immersive hero experiences
 * - Maximizes visual impact of hero sections
 * - Maintains navigation accessibility while preserving design
 * - Allows for different header styles based on page type
 *
 * **Implementation Details:**
 * - `position: absolute` on overlay headers removes them from document flow
 * - Hero sections can extend to full viewport height
 * - Z-index ensures header stays above hero content
 * - Responsive design maintains functionality across devices
 *
 * @param children - Page content to be wrapped
 * @param hasHero - Whether the page has a hero section (affects header positioning)
 * @param className - Additional CSS classes for the main container
 */
const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  hasHero = false,
  className = '',
  isLandingPage = false,
  email,
  showLocation = true,
  showNewsletter = true,
  phone,
  btnTitle,
}) => {
  const pathname = usePathname();
  useScrollAnimation(pathname);

  return (
    <PharmacyBookingModalProvider>
      <div
        className={`flex min-h-screen flex-col overflow-x-hidden ${hasHero ? 'pt-28' : ''}`}
      >
        {/* Header - isOverlay handles transparent/absolute positioning for hero pages */}
        <Header
          isOverlay={hasHero}
          isLandingPage={isLandingPage}
          email={email}
          phone={phone}
          btnTitle={btnTitle}
        />

        {/* Main Content Area */}
        <main className={`flex-1 ${className}`}>{children}</main>

        {showLocation && <LazyLocationSection />}
        {showNewsletter && <LazyNewsletterSection />}
        {/* Footer */}
        <Footer />
        <FloatingMobileActions phone={phone} />
      </div>
    </PharmacyBookingModalProvider>
  );
};

export default MainLayout;
